<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SearchPill from 'dashboard/components-next/search-pill/SearchPill.vue';
import { mobileFloatingActionButtonClasses } from 'dashboard/constants/mobileFloatingActionButton';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
  canManageDirectory: { type: Boolean, default: false },
  canCreateContact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
  'newConversation',
]);
</script>

<template>
  <header
    :class="[
      'max-md:[&_[class*=inner]]:!px-4',
      'max-md:[&_[class*=title-group]]:!w-full',
      'max-md:[&_[class*=title-group]>div]:!w-full',
      'max-md:[&_h1]:!pr-0',
      'max-md:[&_h1]:!text-left',
      'max-md:[&_[class*=subtitle]]:!text-left',
      'max-md:[&_[class*=subtitle]]:!whitespace-normal',
      'max-md:[&_[class*=subtitle]]:!text-xs',
      'max-md:[&_[class*=subtitle]]:!leading-5',
    ]"
    class="contacts-page-header sticky top-0 z-10 px-3 sm:px-6"
  >
    <div
      class="contacts-page-header__inner flex flex-col items-stretch sm:flex-row sm:items-center justify-between w-full py-4 gap-3 sm:gap-4 mx-auto max-w-5xl"
    >
      <span
        class="min-w-0 truncate text-[28px] font-bold text-n-slate-12 max-md:!w-full max-md:!pr-[64px] max-md:!text-center max-md:!text-[25px]"
      >
        {{ headerTitle }}
      </span>
      <div
        class="flex w-full min-w-0 flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center sm:flex-shrink-0 sm:gap-4"
      >
        <div
          v-if="showSearch"
          class="flex min-w-0 flex-1 items-center sm:w-auto sm:flex-none"
        >
          <SearchPill
            :model-value="searchValue"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            clear-label="Limpar busca"
            type="search"
            class="w-full md:hidden"
            @input="emit('search', $event.target.value)"
            @clear="emit('search', '')"
          />
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'contacts-page-header__search-input !h-11 !min-h-11 !rounded-full !border-0 !bg-[#f5f7fb] ltr:!pl-10 rtl:!pr-10 !text-sm',
            ]"
            class="contacts-page-header__search-field group hidden min-h-11 w-full md:block"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-10 size-4 top-1/2 ltr:left-3 rtl:right-3 z-10"
              />
            </template>
          </Input>
        </div>
        <div
          class="flex w-full flex-shrink-0 items-center justify-between gap-1.5 sm:w-auto sm:justify-start sm:gap-4"
        >
          <div
            class="acl-mobile-account-menu-host relative flex w-full min-w-0 items-center justify-end gap-1.5 sm:w-auto sm:justify-start sm:gap-2"
          >
            <div
              v-if="!isLabelView && !isActiveView"
              class="relative max-md:static"
            >
              <Button
                id="toggleContactsFilterButton"
                :icon="
                  isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
                "
                color="slate"
                size="sm"
                class="relative w-8 max-md:!text-[#525252]"
                :class="[
                  mobileFloatingActionButtonClasses,
                  {
                    'max-md:!text-[#15603e]':
                      hasActiveFilters || isSegmentsView,
                  },
                ]"
                variant="ghost"
                @click="emit('filter')"
              >
                <div
                  v-if="hasActiveFilters && !isSegmentsView"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <slot name="filter" />
            </div>
            <Button
              v-if="
                hasActiveFilters &&
                !isSegmentsView &&
                !isLabelView &&
                !isActiveView
              "
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView && !isActiveView"
              icon="i-lucide-trash"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('deleteSegment')"
            />
            <ContactSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <ContactMoreActions
              :can-manage-directory="canManageDirectory"
              :can-create-contact="canCreateContact"
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
            />
            <Button
              :label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
              size="sm"
              class="max-md:order-first max-md:mr-auto md:hidden flex-shrink-0 max-md:!rounded-full max-md:!px-4 max-md:!shadow-sm"
              @click="emit('newConversation')"
            />
          </div>
          <div class="hidden sm:block w-px h-4 bg-n-strong" />
          <!-- Aceleres: usa o mesmo fluxo do Chat de Atendimento (painel de
               contato/telefone + seleção de instância), montado no wrapper. -->
          <Button
            :label="buttonLabel"
            size="sm"
            class="hidden flex-shrink-0 sm:inline-flex"
            @click="emit('newConversation')"
          />
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.contacts-page-header {
  background: #f6f6f6;
  box-shadow: 0 1px 0 rgba(226, 232, 240, 0.9);
  isolation: isolate;
}

.contacts-page-header::before {
  content: '';
  position: absolute;
  inset: 0 -1.5rem;
  z-index: -1;
  background: #f6f6f6;
}

.contacts-page-header__inner {
  box-sizing: border-box;
}

.contacts-page-header__search-field :deep(.contacts-page-header__search-input) {
  height: 44px !important;
  min-height: 44px !important;
  line-height: 44px !important;
  background: rgba(194, 189, 184, 0.15) !important;
}
</style>
